// feature/eva-litisoft — Cola de carga (Eva tab). The run-creation surface,
// rebuilt operator-first: the SAFE path (simulación / dry-run) is the default
// and primary; the real upload is a deliberate, visually-distinct, CONFIRMED
// action — because that's the click that writes to a real legal portal. Reuses
// the Phase-1/2 endpoints unchanged. Rendered inside the Eva hub (no page h1).
import React, { useEffect, useMemo, useState } from "react";
import { bot } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import Confirm from "../../components/Confirm";
import { useToast } from "../../components/Toast";
import { useCaseLauncher } from "../../components/CaseLauncher";

const fmt = (d) => (d ? new Date(d).toLocaleString("es-CO") : "—");

// feature/bot-litisoft-paste-selector — digits-only key, mirrors Athena's
// normalize_radicado (A11: strip every non-digit, leading zeros KEPT) so a
// pasted radicado matches an eligible row byte-for-byte regardless of the dots,
// hyphens, en-dashes or spaces the operator pasted.
const normRadicado = (s) => String(s || "").replace(/\D/g, "");

export default function BotLitisoftCola({ onChanged }) {
  const toast = useToast();
  const launcher = useCaseLauncher();
  const [items, setItems] = useState(null);
  const [runs, setRuns] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState("sim"); // 'sim' (dry-run) | 'real'
  const [busy, setBusy] = useState(false);
  const [confirmReal, setConfirmReal] = useState(false);
  // feature/bot-litisoft-paste-selector — paste a radicado list, auto-select
  // the matching eligible rows (so the junio batch is one paste, not 100 boxes).
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteResult, setPasteResult] = useState(null);

  function loadRuns() {
    bot.runs().then((r) => setRuns(r.data.runs)).catch(() => setRuns([]));
  }
  function load() {
    setItems(null);
    bot.reconciliation()
      .then((r) => setItems((r.data.items || []).filter((i) => i.eligible)))
      .catch(() => setItems([]));
    loadRuns();
  }
  useEffect(() => { load(); }, []);

  const allSelected = useMemo(
    () => items && items.length > 0 && items.every((i) => selected.has(i.id)),
    [items, selected]
  );
  function toggle(id) {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSelected((prev) => (items && items.every((i) => prev.has(i.id))) ? new Set() : new Set((items || []).map((i) => i.id)));
  }

  // feature/bot-litisoft-paste-selector — parse the pasted text into
  // digits-only keys, then check each eligible row's normalized radicado
  // against that set and add the matches to the selection. Pasted radicados
  // that don't hit an eligible row (accumulado, sin caso, ya en LITISOFT, o
  // forma corta) are surfaced so the operator knows what was left out.
  function applyPaste() {
    // Split on line breaks, '/', commas and semicolons — NOT on whitespace:
    // Colombian radicados are conventionally written with spaces between their
    // segments (e.g. "11001 31 87 024 2026 00096 00"), so splitting on spaces
    // would shatter one radicado into fragments. normRadicado strips the inner
    // spaces instead. Mirrors the backend script's newline+'/' tokenizer.
    const parsed = pasteText
      .split(/[\r\n,;/]+/)
      .map((t) => ({ raw: t.trim(), key: normRadicado(t) }))
      .filter((t) => t.key);
    if (!parsed.length) {
      setPasteResult(null);
      toast.push("No encontré radicados en el texto pegado", "error");
      return;
    }
    // Distinct pasted keys (keep the first raw form for display).
    const rawByKey = new Map();
    for (const t of parsed) if (!rawByKey.has(t.key)) rawByKey.set(t.key, t.raw);
    const itemByKey = new Map((items || []).map((it) => [normRadicado(it.radicado), it]));
    const hitIds = [];
    const unmatched = [];
    for (const [key, raw] of rawByKey) {
      const it = itemByKey.get(key);
      if (it) hitIds.push(it.id);
      else unmatched.push(raw);
    }
    setSelected((prev) => {
      const n = new Set(prev);
      hitIds.forEach((id) => n.add(id));
      return n;
    });
    setPasteResult({ selected: hitIds.length, pasted: rawByKey.size, unmatched });
    toast.push(
      `Seleccioné ${hitIds.length} de ${rawByKey.size} radicado(s) pegados`,
      hitIds.length ? "success" : "error"
    );
  }

  async function crearCorrida(dryRun) {
    const ids = [...selected];
    if (!ids.length) { toast.push("Selecciona al menos un caso", "error"); return; }
    setBusy(true);
    try {
      const r = await bot.createRun({ kind: "upload", case_ids: ids, dry_run: dryRun });
      toast.push(
        dryRun
          ? `Simulación #${r.data.run_id} creada (${r.data.item_count} casos). Míra la en el Monitor — no se sube nada.`
          : `Carga real #${r.data.run_id} creada (${r.data.item_count} casos). Eva la radicará en LITISOFT.`,
        "success"
      );
      setSelected(new Set());
      load();
      onChanged?.();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.rejected) toast.push(`Algunos casos ya no son elegibles (${d.rejected.length}).`, "error");
      else toast.push(d?.error || "Error", "error");
    } finally { setBusy(false); setConfirmReal(false); }
  }

  const n = selected.size;
  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="muted">
        Estos son los casos que puedo radicar: coinciden con un único proceso, tienen hechos y aún no
        tienen número LITISOFT. Selecciona los que quieras y <strong>simulamos primero</strong> — te
        muestro qué haría en cada uno sin tocar el portal.
      </div>

      {/* Mode selector — safe path is the default, visually primary. */}
      <div className="card" style={{ padding: 14, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", borderRadius: "var(--r-btn)", overflow: "hidden", border: "1px solid var(--border-hairline)" }}>
          <button className={mode === "sim" ? "" : "ghost"} style={{ borderRadius: 0, height: 34 }} onClick={() => setMode("sim")}>
            Simulación
          </button>
          <button className={mode === "real" ? "danger" : "ghost"} style={{ borderRadius: 0, height: 34 }} onClick={() => setMode("real")}>
            Carga real
          </button>
        </div>

        {mode === "sim" ? (
          <button disabled={busy || !n} onClick={() => crearCorrida(true)}>
            {busy && <Spinner color="currentColor" />} Simular {n || ""} caso{n === 1 ? "" : "s"}
          </button>
        ) : (
          <button className="danger" disabled={busy || !n} onClick={() => setConfirmReal(true)}>
            Radicar {n || ""} de verdad en LITISOFT
          </button>
        )}

        <span className="muted" style={{ fontSize: 12.5, flex: 1, minWidth: 200 }}>
          {mode === "sim"
            ? "La simulación no escribe nada en LITISOFT — es para revisar."
            : "⚠ La carga real escribe en el portal. Hazla solo después de revisar una simulación."}
        </span>
      </div>

      {/* feature/bot-litisoft-paste-selector — paste a radicado list and
          auto-select the matching eligible rows (one paste, not 100 boxes). */}
      <div className="card" style={{ padding: 14 }}>
        <button
          className="ghost"
          onClick={() => setPasteOpen((v) => !v)}
          style={{ border: "none", padding: 0, height: "auto", fontWeight: 600, fontSize: 13 }}
        >
          {pasteOpen ? "▾" : "▸"} Pegar lista de radicados
        </button>
        {pasteOpen && (
          <div className="col" style={{ gap: 8, marginTop: 10 }}>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Pega los radicados, uno por línea (o separados por comas). Marco
              automáticamente los casos elegibles que coincidan — ignoro puntos, guiones
              y espacios dentro de cada radicado, comparo solo los dígitos.
            </div>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={5}
              placeholder={"11001 31 87 024 2026 00096 00\n13001-31-09-003-2026-00072-00\n…"}
              style={{ width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button className="secondary" disabled={!pasteText.trim()} onClick={applyPaste}>
                Seleccionar coincidencias
              </button>
              {(pasteText || pasteResult) && (
                <button className="ghost" onClick={() => { setPasteText(""); setPasteResult(null); }}>
                  Limpiar
                </button>
              )}
            </div>
            {pasteResult && (
              <div className="muted" style={{ fontSize: 12 }}>
                Seleccioné <strong>{pasteResult.selected}</strong> de {pasteResult.pasted} radicado(s) pegados.
                {pasteResult.unmatched.length > 0 && (
                  <>
                    {" "}{pasteResult.unmatched.length} no coincidieron con un caso elegible
                    (acumulado, sin caso, ya en LITISOFT, o forma corta):
                    <div style={{ fontFamily: "monospace", fontSize: 11, marginTop: 4, maxHeight: 120, overflowY: "auto" }}>
                      {pasteResult.unmatched.join(",  ")}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {items == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !items.length ? (
          <Empty title="No tengo casos listos ahora" message="Aparecen aquí cuando un caso coincide con un único proceso y tiene hechos." />
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}><input type="checkbox" checked={!!allSelected} onChange={toggleAll} /></th>
                <th>Caso</th><th>Radicado</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} /></td>
                  <td onClick={() => launcher.open(it.id)} style={{ cursor: "pointer" }}><strong>{it.case_number}</strong></td>
                  <td className="tnum" style={{ fontFamily: "monospace", fontSize: 12 }}>{it.radicado}</td>
                  <td>{it.estado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 style={{ margin: "8px 0 0", fontSize: 15 }}>Corridas recientes</h2>
      <div className="card" style={{ padding: 0 }}>
        {runs == null ? (
          <div style={{ padding: 24, textAlign: "center" }}><Spinner size={18} /></div>
        ) : !runs.length ? (
          <Empty title="Todavía no hemos hecho ninguna corrida" />
        ) : (
          <table>
            <thead><tr><th>#</th><th>Tipo</th><th>Estado</th><th>Modo</th><th>Ítems</th><th>Creada</th><th>Por</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td><td>{r.kind}</td><td>{r.status}</td>
                  <td>{r.dry_run ? "Simulación" : "Real"}</td>
                  <td className="tnum">{r.completed_items}/{r.total_items}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmt(r.created_at)}</td>
                  <td>{r.requested_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Confirm
        open={confirmReal}
        danger
        title="¿Radicar de verdad en LITISOFT?"
        message={`Voy a radicar ${n} caso${n === 1 ? "" : "s"} directamente en el portal de LITISOFT. Esto sí escribe de verdad y queda registrado. Hazlo solo si ya revisaste una simulación de estos casos.`}
        confirmText={`Sí, radicar ${n}`}
        cancelText="Cancelar"
        onClose={() => setConfirmReal(false)}
        onConfirm={() => crearCorrida(false)}
      />
    </div>
  );
}
